import { useState, useEffect } from 'react';
import { Bus, Menu, X, ArrowRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = ({ handleTransport, handleBookNow }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    handleScroll(); // set the right style if the page loads already scrolled
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile menu when the page changes or Esc is pressed
  useEffect(() => setIsOpen(false), [pathname]);
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setIsOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const handleNavClick = (id) => {
    setIsOpen(false); // close mobile menu
    switch (id) {
      case 'transport':
        handleTransport(); // scroll to Transport
        break;
      case 'booking':
        handleBookNow(); // scroll to Transport (Book Transport)
        break;
      case 'accommodation':
        navigate('/accommodation'); // navigate to accommodation page
        break;
      case 'contact':
        navigate('/contact'); // navigate to contact page
        break;
      case 'hero':
        navigate('/');
        break;
      default:
        break;
    }
  };

  // "Book Now" has its own button, so it isn't repeated as a link
  const navItems = [
    { label: 'Transport', id: 'transport' },
    { label: 'Accommodation', id: 'accommodation', path: '/accommodation' },
    { label: 'Contact', id: 'contact', path: '/contact' },
  ];

  const isActive = (item) => item.path && pathname.startsWith(item.path);

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
        isScrolled || isOpen ? 'bg-white shadow-md' : 'bg-white/80 shadow-sm backdrop-blur-md'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main">
        <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'py-3' : 'py-4'}`}>
          {/* Logo */}
          <button
            onClick={() => handleNavClick('hero')}
            className="group flex items-center gap-3"
            aria-label="V2V home"
          >
            <div className="rounded-xl bg-gradient-to-br from-orange-600 to-yellow-500 p-2 shadow-sm transition group-hover:shadow-md group-hover:shadow-orange-200">
              <Bus className="h-6 w-6 text-white" />
            </div>
            <div className="flex flex-col items-start">
              <span className="bg-gradient-to-r from-orange-700 to-orange-500 bg-clip-text text-2xl font-extrabold leading-tight tracking-tight text-transparent">
                V2V
              </span>
              <span className="text-[11px] font-semibold leading-none text-gray-500">
                Transport & Accommodation
              </span>
            </div>
          </button>

          {/* Desktop links */}
          <div className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => {
              const active = isActive(item);
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  aria-current={active ? 'page' : undefined}
                  className={`group relative rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    active ? 'text-gray-900' : 'text-gray-600 hover:text-orange-700'
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute bottom-0.5 left-4 right-4 h-0.5 origin-left rounded-full bg-yellow-400 transition-transform duration-300 ${
                      active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            {/* Book Now (tablet and desktop) */}
            <button
              onClick={() => handleNavClick('booking')}
              className="group hidden items-center gap-2 rounded-xl bg-orange-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-orange-800 hover:shadow-lg hover:shadow-orange-200 sm:inline-flex"
            >
              Book Now
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </button>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsOpen((o) => !o)}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
              aria-controls="mobile-menu"
              className="rounded-lg p-2 transition hover:bg-gray-100 md:hidden"
            >
              {isOpen ? <X className="h-6 w-6 text-gray-900" /> : <Menu className="h-6 w-6 text-gray-900" />}
            </button>
          </div>
        </div>

        {/* Mobile menu (slides open smoothly, no extra plugin needed) */}
        <div
          id="mobile-menu"
          className={`grid transition-all duration-300 md:hidden ${
            isOpen ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="flex flex-col gap-1 border-t border-gray-100 pb-5 pt-3">
              {navItems.map((item) => {
                const active = isActive(item);
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    aria-current={active ? 'page' : undefined}
                    className={`rounded-xl px-4 py-3 text-left font-medium transition ${
                      active
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'text-gray-700 hover:bg-orange-50 hover:text-orange-700'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
              <button
                onClick={() => handleNavClick('booking')}
                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-700 px-4 py-3 font-semibold text-white transition hover:bg-orange-800"
              >
                Book Now
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;